import React from "react";
import { Button, IconButton, Snackbar, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Close as CloseIcon, GetApp as GetAppIcon } from "@material-ui/icons";
import {
  isInstallBannerDismissed,
  dismissInstallBanner,
  isIos,
  isStandalone,
} from "utils/installBanner";

// Non-intrusive - a bottom Snackbar (same idiom every save confirmation
// in this app already uses), not a blocking modal, and never forced back
// on someone who's dismissed it once (see utils/installBanner.js).
// Mounted once in App.js, outside the route Switch, so it survives
// navigation instead of re-running its "have we already checked" logic
// on every page.
export default () => {
  // Only Chromium browsers (Chrome/Edge/Samsung Internet on Android,
  // desktop Chrome) ever fire this - capturing it is also what lets
  // clicking "Install" trigger the browser's real native install dialog
  // instead of just linking out to instructions.
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [dismissed, setDismissed] = React.useState(isInstallBannerDismissed);

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    // Fires once the browser considers the app actually installed -
    // covers both this banner's own Install button and the OS's/
    // browser's own separate install affordance (e.g. Chrome's
    // address-bar icon), so either path dismisses it the same way.
    const handleAppInstalled = () => {
      dismissInstallBanner();
      setDismissed(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleDismiss = () => {
    dismissInstallBanner();
    setDismissed(true);
  };

  const handleInstallClick = () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    // The prompt can only ever be used once - whatever they choose,
    // there's nothing left to show a button for afterward.
    deferredPrompt.userChoice.finally(() => setDeferredPrompt(null));
  };

  // iOS never fires beforeinstallprompt at all (Apple doesn't support
  // it - Add to Home Screen is a manual Safari-menu action, nothing a
  // page can trigger), so that's its own branch below with no Install
  // button, just the instructions.
  const showForAndroid = !!deferredPrompt;
  const showForIos = isIos();
  const open = !dismissed && !isStandalone() && (showForAndroid || showForIos);

  if (!open) return null;

  return (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={open}
      // No autoHideDuration - stays up until the user acts on it one
      // way or another, exactly like the "notify" permission prompt.
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<GetAppIcon fontSize="inherit" />}
        action={
          <>
            {showForAndroid && (
              <Button color="inherit" size="small" onClick={handleInstallClick}>
                Install
              </Button>
            )}
            <IconButton
              size="small"
              color="inherit"
              aria-label="Dismiss"
              title="Dismiss"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {showForAndroid ? (
          "Install PlanPraise for quick access and notifications."
        ) : (
          <Typography variant="body2" component="span">
            Install PlanPraise: tap Share, then "Add to Home Screen".
          </Typography>
        )}
      </Alert>
    </Snackbar>
  );
};
